import React, { useState, Fragment, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Dropdown, Form, TextArea, Transition, Button } from 'semantic-ui-react';
import i18next from 'i18next';
import { usePageTitle } from '../../hooks';
import paiditApi, { POPUP } from '../../api/paidit-api';
import { getAssets } from '../../selectors';
import SearchAddress from './SearchAddress';
import { PILink } from '../nav';
import { useTransition, useOrderType } from '../../hooks';
import { saveDeliveryInfo, pushAlert } from '../../actions';
import { setBranch, resetBranch } from '../../actions/BusinessActions';

import './DeliveryInfo.scss';

const DeliveryInfo = () => {
    const addressTypes = ['apartment', 'office', 'house'];

    const initialFormState = {
        addressType: addressTypes[0],
        address: '',
        entrance: '',
        floor: '',
        apartment: '',
        remarks: ''
    };

    const dispatch = useDispatch();
    const [form, setForm] = useState(initialFormState);
    const [loading, setLoading] = useState(false);
    const branch = useSelector(state => state.business.branch);
    const { mainBackground } = useSelector(getAssets);
    const chainId = useSelector(state => state.business.chain.id);
    const visible = useTransition();

    usePageTitle('pageTitles.deliveryInfo');
    useOrderType('delivery');
    useEffect(() => {
        dispatch(resetBranch());
    }, [dispatch]);

    const handleAddressChange = async (address) => {
        setLoading(true);

        const eligbleBranches = await paiditApi().delivery.getEligbleRestaurants(address.geometry.location, chainId);
        const branch = eligbleBranches && eligbleBranches[0] && eligbleBranches[0];

        if (!branch) {
            dispatch(pushAlert({ type: POPUP, text: i18next.t('order.address.branchNotFound') }));
        }

        setLoading(false);
        setForm({ ...form, address: address.formatted_address, coordinates: address.geometry.location });
        dispatch(setBranch(branch));
    };

    return (
        <div className="pi-delivery" style={{ backgroundImage: `url(${mainBackground})`, backgroundSize: 'cover' }}>
            <Transition
                animation={'scale'}
                duration={500}
                visible={visible}
            >
                <Card className="pi-container" raised>
                    <Form className="delivery-form">
                        <Form.Field>
                            <label>{i18next.t('order.address.instructions')}
                                <SearchAddress onAddressChanged={handleAddressChange} isLoading={loading}/>
                            </label>
                        </Form.Field>
                        <Form.Field>
                            <label>{i18next.t('order.address.type')}
                                <Dropdown
                                    disabled={!branch}
                                    fluid
                                    selection
                                    options={addressTypes.map(value => ({ value, text: i18next.t('order.address.' + value) }))}
                                    onChange={(e, { value }) => { setForm({ ...form, addressType: value }); }}
                                    value={form.addressType}
                                />
                            </label>
                        </Form.Field>
                        <Form.Group widths="equal">
                            {form.addressType === 'apartment' && 
                                <Form.Input 
                                    placeholder={i18next.t('order.address.entrance')} 
                                    fluid 
                                    disabled={!branch} 
                                    onChange={(e, { value }) => { setForm({ ...form, entrance: value }); }}
                                />}
                            {form.addressType !== 'house' && (
                                <Fragment>
                                    <Form.Input 
                                        type="number" 
                                        placeholder={i18next.t('order.address.floor')} 
                                        fluid 
                                        disabled={!branch} 
                                        onChange={(e, { value }) => { setForm({ ...form, floor: value }); }}
                                    />
                                    <Form.Input 
                                        type="number" 
                                        placeholder={i18next.t('order.address.apartment')} 
                                        disabled={!branch} 
                                        fluid 
                                        onChange={(e, { value }) => { setForm({ ...form, apartment: value }); }}
                                    />
                                </Fragment>
                            )}
                        </Form.Group>
                        <Form.Field className="remarks">
                            <TextArea 
                                placeholder={i18next.t('order.address.remarks')} 
                                disabled={!branch} 
                                onChange={(e, { value }) => { setForm({ ...form, remarks: value }); }}
                            />
                        </Form.Field>
                        <Form.Group className="pi actions">
                            <Button className="cancel-btn" as={PILink} to={'/'}>
                                {i18next.t('general.cancel')}
                            </Button>
                            <Button className="approve-btn" onClick={() => dispatch(saveDeliveryInfo(form))} as={PILink} to={'/order'} disabled={!branch}>
                                {i18next.t('general.approve')}
                            </Button>
                        </Form.Group>
                    </Form>
                </Card>
            </Transition>
        </div>
    );
};

export default DeliveryInfo;
